import React, { Fragment, memo, useMemo, useState } from "react"

import { useTranslation } from "react-i18next"
import { useQuery } from "react-query"
import { useDispatch, useSelector } from "react-redux"
import { useLocation, useNavigate } from "react-router"

import MoreHorizIcon from "@mui/icons-material/MoreHoriz"
import { Card, Grid, IconButton } from "@mui/material"
import { Menu, MenuItem, Skeleton, Typography } from "@synapse-analytics/synapse-ui"
import moment from "moment"

import { KonanAvatarComponent } from "../../components/core/KonanAvatar"
import { RequestsDailyBarChart } from "../../components/graphs/Bar/RequestsDailyBarChart"
import { ProjectType } from "../../components/status/ProjectType"
import { KonanAPI } from "../../services/KonanAPI"
import type { RootState } from "../../store/ReduxStore"
import type { RequestSummaryDailyOutput } from "../../types/generated/api/RequestSummaryDailyOutput"
import { useIsPermitted } from "../../utils/PermissionsHelpers"
import { KonanTimeHelper, commify } from "../../utils/genericHelpers"
import { setCurrentProject } from "./Slice"
import type { CardMenuProps, PredictionsContainerProps, ProjectCardProps } from "./interfaces"

import Styles from "./Projects.module.scss"

/**
 * Predictions container, which holds
 * 1. Predictions graph
 * 2. loading state
 * @param  {number} totalPredictionsCount
 * @param  {RequestSummaryDailyOutput} dailyPredictionDates
 * @param  {boolean} isPredictionDatesLoading
 * @return  {React.ReactElement}
 */
function PredictionsContainer(props: Readonly<PredictionsContainerProps>): React.ReactElement {
  const { totalPredictionsCount, dailyPredictionDates, isPredictionDatesLoading } = props
  const { t } = useTranslation("projects")

  return (
    <Fragment>
      {totalPredictionsCount ? (
        <Grid container item xs={12} className={Styles.projectPredictionContainer}>
          <Grid item container className={Styles.projectPredictionTypography}>
            <Grid item>
              <Typography className={Styles.projectTotalPredictions} variant="label-regular">
                {commify(totalPredictionsCount)}
              </Typography>
            </Grid>

            <Grid item>
              <Typography variant="label-regular" className={Styles.projectLast28}>
                {t("card.last28Days")}
              </Typography>
            </Grid>
          </Grid>

          <Grid item xs={12}>
            <RequestsDailyBarChart
              data={dailyPredictionDates ? dailyPredictionDates?.data : []}
              graphHeight={40}
              isLoading={isPredictionDatesLoading}
            />
          </Grid>
        </Grid>
      ) : (
        <Grid className={Styles.projectEmptyState} container item xs={12}>
          <Typography variant="a" className={Styles.projectEmptyStateTitle}>
            {t("card.noPredictions")}
          </Typography>
        </Grid>
      )}
    </Fragment>
  )
}

/**
 * Project overview card
 * @param  {Project} project
 * @param  {function} onProjectDeletionClicked
 * @return  {React.ReactElement}
 */
export const ProjectCard = memo(function ProjectCard(props: Readonly<ProjectCardProps>): React.ReactElement {
  const { project, onProjectDeletionClicked } = props
  const { t } = useTranslation("projects")
  const permissions = useSelector((state: RootState) => state.auth.permissions)

  const flattenedKonanPermissions = useSelector((state: RootState) => state.permissions.flattenedKonanPermissions)

  const canDeleteProject = useIsPermitted("Delete project", permissions.konan, flattenedKonanPermissions)

  const dispatch = useDispatch()
  const navigate = useNavigate()
  const { pathname } = useLocation()

  const konanTime = new KonanTimeHelper()
  const predictionsStartDate = konanTime.adjustDate(moment().subtract(27, "days"), "start")?.toISOString() as string
  const now = konanTime.adjustDate(moment(), "end")?.toISOString() as string

  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null)

  // fetching model request summary in the prediction graph
  const { isLoading: isPredictionDatesLoading, data: dailyPredictionDates } = useQuery<RequestSummaryDailyOutput>(
    ["project-daily-request-summary", project.uuid],
    () =>
      KonanAPI.fetchDailyProjectRequestSummary({
        project_uuid: project.uuid,
        start_date: predictionsStartDate,
        end_date: now,
      }),
    {
      enabled: !!project.uuid,
    },
  )

  // memoizing total live model predictions to avoid heavy recalculations
  const totalPredictionsCount = useMemo(() => {
    let liveModelTotalPredctions = 0
    dailyPredictionDates?.data.forEach((item) => {
      liveModelTotalPredctions += item.successful + item.failed
    })

    return liveModelTotalPredctions
  }, [dailyPredictionDates])

  // handler for selecting a project to navigate to, setting the currentProject context with
  // the initial project info we have untill we fetch the whole project,
  // this will eliminate extra unnecessary loading state
  const handleProjectClick = (): void => {
    // set the currentProject with the initial data we have

    dispatch(setCurrentProject(project))

    // navigate the route
    navigate(`${pathname}/${project.uuid}/${project.category === "workflow" ? "workflows" : "overview"}`)
  }

  return (
    <Card className={"card-box-shadow"}>
      <Grid container direction="column" className={Styles.projectCardHeaderRoot}>
        {project.category === "model" && (
          <Grid item xs={12} container justifyContent="space-between" alignItems="center" mt={"-4px"}>
            <Grid item container xs>
              <ProjectType type={project.type} size="small" />
            </Grid>

            {canDeleteProject && (
              <CardMenu
                anchorEl={anchorEl}
                setAnchorEl={(anchor: HTMLElement | null) => {
                  setAnchorEl(anchor)
                }}
                onProjectDeletionClicked={() => onProjectDeletionClicked(project.name, project.uuid)}
              />
            )}
          </Grid>
        )}

        <Grid item xs={12} container justifyContent="space-between" alignItems="flex-start" wrap="nowrap">
          <Grid item xs={10} md={11}>
            <Typography
              variant="h2-bold"
              noWrap
              onClick={handleProjectClick}
              className={Styles.projectHeader}
              color="important"
              variantColor={2}
              style={{ width: "100% - 50px" }}
            >
              {project.name}
            </Typography>
          </Grid>

          {project.category === "workflow" && canDeleteProject && (
            <Grid item>
              <CardMenu
                anchorEl={anchorEl}
                setAnchorEl={(anchor: HTMLElement | null) => {
                  setAnchorEl(anchor)
                }}
                onProjectDeletionClicked={() => onProjectDeletionClicked(project.name, project.uuid)}
              />
            </Grid>
          )}
        </Grid>

        <Grid container item xs={12} spacing={1} alignItems="flex-start" className={Styles.projectCardAvatarContainer}>
          <KonanAvatarComponent
            createdBy={project.created_by}
            title={project.created_by}
            createdAt={project.created_at}
          />
        </Grid>
      </Grid>

      {/* predictions chart section */}
      <Grid container item direction="row" className={Styles.projectBodyRoot}>
        <Grid item container direction="row" marginBottom={1}>
          <Typography variant="label-bold" tooltip={t("card.predictionsTooltip")} tooltipPlacement="right">
            {t("card.predictions")}
          </Typography>
        </Grid>

        <Grid container className={Styles.projectSectionBox}>
          {isPredictionDatesLoading ? (
            <Grid container className={Styles.projectSectionBoxLoader}>
              <Skeleton variant="rectangular" height={80} />
            </Grid>
          ) : (
            <PredictionsContainer
              totalPredictionsCount={totalPredictionsCount}
              dailyPredictionDates={dailyPredictionDates}
              isPredictionDatesLoading={isPredictionDatesLoading}
            />
          )}
        </Grid>
      </Grid>
    </Card>
  )
})

const CardMenu = (props: Readonly<CardMenuProps>): React.ReactElement => {
  const { t } = useTranslation("projects")
  const { anchorEl, setAnchorEl, onProjectDeletionClicked } = props
  return (
    <Grid item>
      <IconButton
        size="small"
        aria-label="settings"
        id="ProjectCard_settings"
        onClick={(event: React.MouseEvent<HTMLButtonElement>) => setAnchorEl(event.currentTarget)}
        className={Styles.projectHeaderIconBtn}
      >
        <MoreHorizIcon />
      </IconButton>
      <Menu
        menuMaxContent
        id="simple-menu"
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={() => setAnchorEl(null)}
      >
        <MenuItem
          onClick={() => {
            setAnchorEl(null)
            onProjectDeletionClicked()
          }}
          id="delete_project"
        >
          <Typography className={Styles.projectDeleteButton} variant="a">
            {t("buttons.deleteProject")}
          </Typography>
        </MenuItem>
      </Menu>
    </Grid>
  )
}
